import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Simple Coptic Cross
const CopticCross = ({ className = "", size = "md" }: { className?: string; size?: "sm" | "md" }) => {
  const dims: Record<string, { outer: number; arm: number; terminal: number; center: number }> = {
    sm: { outer: 18, arm: 4, terminal: 4, center: 6 },
    md: { outer: 24, arm: 5, terminal: 5, center: 8 },
  };
  const d = dims[size];
  const half = d.outer / 2;
  const termR = d.terminal / 2;
  const armHalf = d.arm / 2;

  return (
    <div className={`relative flex-shrink-0 ${className}`} style={{ width: d.outer, height: d.outer }}>
      <div className="absolute bg-[#C9A227]" style={{ left: half - armHalf, top: termR, width: d.arm, height: d.outer - termR * 2 }} />
      <div className="absolute bg-[#C9A227]" style={{ top: half - armHalf, left: termR, height: d.arm, width: d.outer - termR * 2 }} />
      <div className="absolute rounded-full bg-[#C9A227]" style={{ width: d.terminal, height: d.terminal, left: half - termR, top: 0 }} />
      <div className="absolute rounded-full bg-[#C9A227]" style={{ width: d.terminal, height: d.terminal, left: half - termR, bottom: 0 }} />
      <div className="absolute rounded-full bg-[#C9A227]" style={{ width: d.terminal, height: d.terminal, top: half - termR, left: 0 }} />
      <div className="absolute rounded-full bg-[#C9A227]" style={{ width: d.terminal, height: d.terminal, top: half - termR, right: 0 }} />
      <div className="absolute rounded-full border border-[#C9A227] bg-[#3d1a1e]" style={{ width: d.center, height: d.center, left: half - d.center / 2, top: half - d.center / 2 }} />
    </div>
  );
};

export default function Footer() {
  const { t } = useTranslation();

  const churchLinks = [
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.services'), path: '/services' },
    { label: t('nav.calendar'), path: '/calendar' },
    { label: t('nav.sermons'), path: '/sermons' },
    { label: t('nav.news'), path: '/news' },
  ];

  const resourceLinks = [
    { label: t('footer.resourcesHub'), path: '/resources' },
    { label: t('footer.baptismForm'), path: '/resources/baptism-form' },
    { label: t('footer.marriageForm'), path: '/resources/marriage-form' },
    { label: t('footer.funeralForm'), path: '/resources/funeral-form' },
    { label: t('footer.eventBooking'), path: '/resources/event-booking-form' },
  ];

  const connectLinks = [
    { label: t('nav.contact'), path: '/contact' },
    { label: t('nav.donate'), path: '/donate' },
    { label: t('footer.membershipForm'), path: '/resources/membership-form' },
    { label: t('footer.engagementForm'), path: '/resources/engagement-form' },
  ];

  return (
    <footer className="bg-[#3d1a1e] text-white relative overflow-hidden">

      {/* Subtle gold top line */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 pt-14 pb-10">

        {/* Logo row + cross divider */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[#C9A227]/25" />
          <CopticCross size="md" className="mx-5 opacity-60" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[#C9A227]/25" />
        </div>

        <div className="grid md:grid-cols-4 gap-10 mb-10">
          {/* Column 1: Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img
                src="/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png"
                alt="Saint Mina & Pope Cyril VI Church Logo"
                className="h-11 w-auto"
              />
            </Link>
            <p className="text-sm text-white/75 mb-6 leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-2.5">
              {[
                { icon: 'ri-facebook-fill', label: 'Facebook', url: 'https://facebook.com/StMinaPopeCyrilVI' },
                { icon: 'ri-instagram-line', label: 'Instagram', url: 'https://instagram.com/stminapopecCyrilvi' },
                { icon: 'ri-youtube-fill', label: 'YouTube', url: 'https://youtube.com/@StMinaPopeCyrilVI' },
              ].map(({ icon, label, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-[#C9A227]/40 text-[#C9A227] hover:bg-[#C9A227] hover:text-[#3d1a1e] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Church */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-[#C9A227] mb-5 flex items-center gap-2">
              <CopticCross size="sm" className="opacity-55" />
              {t('footer.church')}
            </h4>
            <ul className="space-y-2.5">
              {churchLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-white/80 hover:text-[#C9A227] transition-colors cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Resources */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-[#C9A227] mb-5 flex items-center gap-2">
              <CopticCross size="sm" className="opacity-55" />
              {t('footer.resources')}
            </h4>
            <ul className="space-y-2.5">
              {resourceLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-white/80 hover:text-[#C9A227] transition-colors cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Connect */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-[#C9A227] mb-5 flex items-center gap-2">
              <CopticCross size="sm" className="opacity-55" />
              {t('footer.connect')}
            </h4>
            <ul className="space-y-2.5">
              {connectLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-white/80 hover:text-[#C9A227] transition-colors cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#C9A227]/15 pt-7">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <p className="text-sm text-white/55">
              {t('footer.copyright')}
            </p>
            <div className="flex gap-5 text-sm text-white/55">
              <a href="#" className="hover:text-[#C9A227] transition-colors cursor-pointer">{t('footer.privacyPolicy')}</a>
              <a href="#" className="hover:text-[#C9A227] transition-colors cursor-pointer">{t('footer.termsOfService')}</a>
            </div>
          </div>
        </div>
      </div>

      {/* Subtle gold bottom line */}
      <div className="h-0.5 bg-gradient-to-r from-transparent via-[#C9A227]/35 to-transparent" />
    </footer>
  );
}